import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useCommunity } from '@/contexts/CommunityContext';
import { 
  useEmailTemplates, 
  useEmailTemplate,
  useCreateEmailTemplate, 
  useUpdateEmailTemplate, 
  useDeleteEmailTemplate,
  type EmailTemplate,
  type CreateEmailTemplateData,
  type UpdateEmailTemplateData
} from '@/hooks/useEmailTemplates';
import EmailPreview from '@/components/admin/EmailPreview';
import { Mail, Edit, Trash2, Plus, Save, X, Eye } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

type TemplateType = 'email_confirmation' | 'password_reset' | 'welcome' | 'business_registration' | 'business_approval' | 'business_listed' | 'pending_subscription_business_listed' | 'listed_for_free' | 'review_notification' | 'offline_notification' | 'job_proposal_notification' | 'proposal_response_notification' | 'admin_business_pending_approval' | 'subscription_pending_completion' | 'incomplete_business_profile' | 'inactive_community_members';

const templateTypeLabels: Record<TemplateType, string> = {
  email_confirmation: 'Email Confirmation',
  password_reset: 'Password Reset',
  welcome: 'Welcome Email',
  business_registration: 'Business Registration',
  business_approval: 'Business Approval',
  business_listed: 'Business Listed',
  pending_subscription_business_listed: 'Pending Subscription Business Listed',
  listed_for_free: 'Listed for Free',
  review_notification: 'Review Notification',
  offline_notification: 'Offline Message Notification',
  job_proposal_notification: 'Job Proposal Notification',
  proposal_response_notification: 'Proposal Response Notification',
  admin_business_pending_approval: 'Admin Business Pending Approval',
  subscription_pending_completion: 'Subscription Pending Completion',
  incomplete_business_profile: 'Incomplete Business Profile',
  inactive_community_members: 'Inactive Community Members'
};

const templateDescriptions: Record<TemplateType, string> = {
  email_confirmation: 'Sent when users need to confirm their email address',
  password_reset: 'Sent when users request a password reset',
  welcome: 'Sent when users join the community',
  business_registration: 'Sent when a business is registered and pending review',
  business_approval: 'Sent when a business is approved and needs to confirm listing',
  business_listed: 'Sent when a business is approved and listed with access',
  pending_subscription_business_listed: 'Sent when a business is listed but needs subscription to interact',
  listed_for_free: 'Campaign email sent to approved businesses encouraging them to confirm their free listing',
  review_notification: 'Sent when a business receives a new published review',
  offline_notification: 'Sent when users receive messages while offline',
  job_proposal_notification: 'Sent to business owners when they receive new service requests',
  proposal_response_notification: 'Sent to users when businesses respond to their service requests',
  admin_business_pending_approval: 'Sent to administrators when a new business registration requires approval',
  subscription_pending_completion: 'Campaign email sent to businesses that need to complete their subscription',
  incomplete_business_profile: 'Campaign email sent to businesses with missing profile information or images',
  inactive_community_members: 'Campaign email sent to community members who haven\'t engaged recently'
};

const variableHelp: Record<TemplateType, string[]> = {
  email_confirmation: ['{{user_name}}', '{{confirmation_url}}', '{{community_name}}', '{{current_year}}'],
  password_reset: ['{{user_name}}', '{{reset_url}}', '{{community_name}}', '{{current_year}}'],
  welcome: ['{{user_name}}', '{{community_name}}', '{{community_url}}', '{{current_year}}'],
  business_registration: ['{{user_name}}', '{{business_name}}', '{{community_name}}', '{{dashboard_url}}', '{{support_email}}', '{{review_timeline}}', '{{current_year}}'],
  business_approval: ['{{user_name}}', '{{business_name}}', '{{community_name}}', '{{community_color}}', '{{listing_confirmation_url}}', '{{subscription_note_html}}', '{{dashboard_url}}', '{{support_email}}', '{{current_year}}'],
  business_listed: ['{{user_name}}', '{{business_name}}', '{{community_name}}', '{{community_color}}', '{{dashboard_url}}', '{{support_email}}', '{{current_year}}'],
  pending_subscription_business_listed: ['{{user_name}}', '{{business_name}}', '{{community_name}}', '{{community_color}}', '{{subscription_url}}', '{{dashboard_url}}', '{{support_email}}', '{{current_year}}'],
  listed_for_free: ['{{user_name}}', '{{business_name}}', '{{community_name}}', '{{community_color}}', '{{listing_confirmation_url}}', '{{dashboard_url}}', '{{support_email}}', '{{current_year}}'],
  review_notification: ['{{business_owner_name}}', '{{business_name}}', '{{reviewer_name}}', '{{review_rating}}', '{{review_date}}', '{{community_name}}', '{{community_color}}', '{{business_page_url}}', '{{support_email}}', '{{current_year}}'],
  offline_notification: ['{{receiver_name}}', '{{sender_name}}', '{{message_content}}', '{{conversation_history}}', '{{community_name}}', '{{conversation_url}}', '{{support_email}}', '{{current_year}}'],
  job_proposal_notification: ['{{business_owner_name}}', '{{business_name}}', '{{service_request_summary}}', '{{service_location}}', '{{service_urgency}}', '{{user_comment}}', '{{review_url}}', '{{community_name}}', '{{community_color}}', '{{support_email}}', '{{current_year}}'],
  proposal_response_notification: ['{{business_name}}', '{{service_request_summary}}', '{{proposal_status}}', '{{service_request_id}}', '{{user_name}}', '{{community_name}}', '{{community_color}}', '{{community_url}}', '{{support_email}}', '{{current_year}}'],
  admin_business_pending_approval: ['{{admin_name}}', '{{business_name}}', '{{business_id}}', '{{business_description}}', '{{business_category}}', '{{business_owner_name}}', '{{business_owner_email}}', '{{contact_email}}', '{{contact_phone}}', '{{full_address}}', '{{website}}', '{{facebook_url}}', '{{instagram_url}}', '{{tiktok_url}}', '{{google_maps_url}}', '{{website_link}}', '{{facebook_link}}', '{{instagram_link}}', '{{tiktok_link}}', '{{google_maps_link}}', '{{discount_type}}', '{{discount_amount_formatted}}', '{{discount_description}}', '{{application_comments}}', '{{registration_date}}', '{{review_url}}', '{{community_name}}', '{{community_color}}', '{{community_url}}', '{{support_email}}', '{{current_year}}'],
  subscription_pending_completion: ['{{user_name}}', '{{business_name}}', '{{subscription_url}}', '{{dashboard_url}}', '{{community_name}}', '{{community_color}}', '{{community_url}}', '{{support_email}}', '{{current_year}}'],
  incomplete_business_profile: ['{{user_name}}', '{{business_name}}', '{{business_profile_url}}', '{{community_name}}', '{{community_color}}', '{{community_url}}', '{{support_email}}', '{{current_year}}'],
  inactive_community_members: ['{{user_name}}', '{{services_url}}', '{{community_name}}', '{{community_color}}', '{{community_url}}', '{{support_email}}', '{{current_year}}']
};

const EmailTemplateManagement = () => {
  const { currentCommunity } = useCommunity();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [isCreating, setIsCreating] = useState<TemplateType | null>(null);
  const [formData, setFormData] = useState<Partial<CreateEmailTemplateData>>({});
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);

  const { data: templates, isLoading } = useEmailTemplates(currentCommunity?.id);
  const createMutation = useCreateEmailTemplate();
  const updateMutation = useUpdateEmailTemplate();
  const deleteMutation = useDeleteEmailTemplate();

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setFormData({
      subject_template: template.subject_template,
      html_template: template.html_template,
      text_template: template.text_template || '',
      sender_name: template.sender_name,
      local_part: template.local_part,
      is_active: template.is_active
    });
  };

  const handleCreate = (templateType: TemplateType) => {
    setIsCreating(templateType);
    setFormData({
      template_type: templateType,
      subject_template: '',
      html_template: '',
      text_template: '',
      sender_name: 'Community Team',
      local_part: 'notifications',
      is_active: true
    });
  };

  const handleSave = async () => {
    if (!currentCommunity?.id) return;

    try {
      if (editingTemplate) {
        await updateMutation.mutateAsync({
          id: editingTemplate.id,
          ...formData as UpdateEmailTemplateData
        });
        setEditingTemplate(null);
      } else if (isCreating) {
        await createMutation.mutateAsync({
          community_id: currentCommunity.id,
          ...formData as CreateEmailTemplateData
        });
        setIsCreating(null);
      }
      setFormData({});
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };

  const handleDelete = async (template: EmailTemplate) => {
    try {
      await deleteMutation.mutateAsync(template.id);
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const handleCancel = () => {
    setEditingTemplate(null);
    setIsCreating(null);
    setFormData({});
  };

  const handlePreview = (template: EmailTemplate) => {
    setPreviewTemplate(template);
  };

  const getTemplateByType = (type: TemplateType): EmailTemplate | undefined => {
    return templates?.find(t => t.template_type === type);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="flex space-x-2">
          <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
          <div className="h-3 w-3 bg-gradient-to-r from-accent to-primary rounded-full animate-pulse animation-delay-200"></div>
          <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse animation-delay-400"></div>
        </div>
      </div>
    );
  }

  if (!currentCommunity) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Please select a community first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      <div className="relative">
        <h1 className="admin-section-title mb-2">Email Templates</h1>
        <p className="text-muted-foreground/80 text-lg">
          Manage email templates for your community
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        {(Object.keys(templateTypeLabels) as TemplateType[])
          .map((templateType) => {
          const existingTemplate = getTemplateByType(templateType);
          const isEditing = editingTemplate?.template_type === templateType;
          const isCreatingThis = isCreating === templateType;
          const showForm = isEditing || isCreatingThis;

          return (
            <Card key={templateType} className={`admin-card admin-card-stats h-fit`}>
              <CardHeader>
                <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-slate-800">{templateTypeLabels[templateType]}</span>
                  </div>
                  {!showForm && (
                    <div className="flex gap-2 justify-end sm:justify-start">
                      {existingTemplate ? (
                        <>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handlePreview(existingTemplate)}
                            title="Preview Email"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(existingTemplate)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="icon" className="h-8 w-8 text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Template</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this email template? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(existingTemplate)}>
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleCreate(templateType)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </CardTitle>
                <p className="text-sm text-muted-foreground/80">
                  {templateDescriptions[templateType]}
                </p>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {!showForm && existingTemplate && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs font-medium text-muted-foreground">Subject</Label>
                      <p className="text-sm">{existingTemplate.subject_template}</p>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-muted-foreground">Sender</Label>
                      <p className="text-sm">{existingTemplate.sender_name} &lt;{existingTemplate.local_part}@{currentCommunity.email_domain || 'community.com'}&gt;</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-muted-foreground">Status</Label>
                      <span className={`text-xs px-2 py-1 rounded-lg font-medium ${existingTemplate.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                        {existingTemplate.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                )}

                {!showForm && !existingTemplate && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Mail className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No template configured</p>
                    <p className="text-xs">Click + to create one</p>
                  </div>
                )}

                {showForm && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="sender_name">Sender Name</Label>
                      <Input
                        id="sender_name"
                        value={formData.sender_name || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, sender_name: e.target.value }))}
                        placeholder="Community Team"
                      />
                    </div>

                    <div>
                      <Label htmlFor="local_part">Email Local Part</Label>
                      <div className="flex items-center">
                        <Input
                          id="local_part"
                          value={formData.local_part || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, local_part: e.target.value }))}
                          placeholder="notifications"
                          className="rounded-r-none min-w-0 flex-1"
                        />
                        <span className="px-2 sm:px-3 py-2 bg-muted text-muted-foreground border border-l-0 rounded-r-md text-xs sm:text-sm whitespace-nowrap">
                          @{currentCommunity.email_domain || 'community.com'}
                        </span>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="subject_template">Subject Template</Label>
                      <Input
                        id="subject_template"
                        value={formData.subject_template || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, subject_template: e.target.value }))}
                        placeholder="Welcome to {{community_name}}!"
                      />
                    </div>

                    <div>
                      <Label htmlFor="html_template">HTML Template</Label>
                      <Textarea
                        id="html_template"
                        value={formData.html_template || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, html_template: e.target.value }))}
                        placeholder="<h1>Welcome {{user_name}}!</h1>"
                        rows={8}
                        className="font-mono text-sm"
                      />
                    </div>

                    <div>
                      <Label htmlFor="text_template">Text Template (Optional)</Label>
                      <Textarea
                        id="text_template"
                        value={formData.text_template || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, text_template: e.target.value }))}
                        placeholder="Welcome {{user_name}}!"
                        rows={4}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="is_active"
                        checked={formData.is_active || false}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                      />
                      <Label htmlFor="is_active">Active</Label>
                    </div>

                    <div className="p-3 bg-muted rounded-lg">
                      <Label className="text-xs font-medium">Available Variables:</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {variableHelp[templateType].map(variable => (
                          <code key={variable} className="text-xs bg-background px-1 py-0.5 rounded">
                            {variable}
                          </code>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button onClick={handleSave} className="flex-1 gap-2">
                        <Save className="h-4 w-4" />
                        Save
                      </Button>
                      <Button variant="outline" onClick={handleCancel} className="gap-2">
                        <X className="h-4 w-4" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Email Preview Modal */}
      {previewTemplate && (
        <EmailPreview
          template={previewTemplate}
          open={!!previewTemplate}
          onOpenChange={(open) => !open && setPreviewTemplate(null)}
        />
      )}
    </div>
  );
};

export default EmailTemplateManagement;
